import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  fetchBrandBySlug,
  clearCurrentBrand,
} from "../store/slices/brandsSlice";
import { Container } from "../components/ui/Section";
import {
  StaggerReveal,
  MotionItem,
  fadeUp,
  fadeUpSmall,
  fadeRight,
  scaleIn,
} from "../components/ui/Motion";
import { GlassCardImage, GlassTag } from "../components/ui/Glass";
import { SkeletonBlock } from "../components/ui/Skeleton";
import {
  mediaUrl,
  pickYachtCardImage,
  pickYachtHeroImage,
  cardImageFallback,
} from "../utils/media";
import { formatCabins } from "../utils/format";
import { ReactComponent as PrincessLogo } from "../assets/icons/princess.svg";
import { ReactComponent as NexLogo } from "../assets/icons/nex.svg";
import { ReactComponent as RibcoLogo } from "../assets/icons/ribco.svg";
import { ReactComponent as AzimutLogo } from "../assets/icons/azimut.svg";
import { ReactComponent as FerrettiLogo } from "../assets/icons/ferretti.svg";
import { ReactComponent as MaioraLogo } from "../assets/icons/maiora.svg";
import { ReactComponent as BenettiLogo } from "../assets/icons/benetti.svg";
import { ReactComponent as SunseekerLogo } from "../assets/icons/sunseeker.svg";
import {
  PageWrapper,
  HeroSection,
  HeroGrid,
  HeroLeft,
  Eyebrow,
  BrandName,
  Tagline,
  BrandMeta,
  MetaItem,
  HeroCtaRow,
  PrimaryCta,
  SecondaryCta,
  HeroVisual,
  HeroLogoMark,
  HeroVisualCaption,
  HeroVisualPlaceholder,
  Section,
  SectionTitle,
  Bio,
  Grid,
  ModelCard,
  ModelTagRow,
  ModelName,
  ModelSubtype,
  ModelSpecs,
  Breadcrumbs,
  NotFound,
  ClassBlock,
  ClassHeader,
  ClassTitle,
  ClassMeta,
  HeroFooter,
} from "./styles/BrandDetailsPage.styles";

const BRAND_LOGO_SVGS = {
  princess: PrincessLogo,
  nex: NexLogo,
  ribco: RibcoLogo,
  azimut: AzimutLogo,
  ferretti: FerrettiLogo,
  maiora: MaioraLogo,
  benetti: BenettiLogo,
  sunseeker: SunseekerLogo,
};

const CATEGORY_COPY = {
  yacht: { label: "Yachts", listingPath: "/yachts" },
};

const CONDITION_TAG = {
  availableNow: { variant: "success", label: "Available Now" },
  brokerage: { variant: "primary", label: "Brokerage" },
  uponRequest: { variant: "uponRequest", label: "Upon Request" },
};

const CLASS_LETTER_ORDER = ["X", "Y", "F", "S", "V", "C", "N", "Venom", "T"];

// Only these brands organize their lineup into named classes (e.g. "X Class").
// Other brands show all models in a single grid without class section headers.
const CLASS_BASED_BRANDS = new Set(["princess"]);

function classDisplayName(code) {
  if (!code) return "Other Models";
  if (code.length === 1) return `${code} Class`;
  return `${code} Class`;
}

const pickCardImage = pickYachtCardImage;

function BrandDetailsPage() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const {
    current: brand,
    currentLoading,
    error,
  } = useSelector((state) => state.brands);

  useEffect(() => {
    dispatch(fetchBrandBySlug(slug));
    return () => {
      dispatch(clearCurrentBrand());
    };
  }, [dispatch, slug]);

  const yachts = useMemo(
    () => (Array.isArray(brand?.yachts) ? brand.yachts : []),
    [brand?.yachts],
  );

  const classGroups = useMemo(() => {
    const map = new Map();
    for (const y of yachts) {
      const code = y.classCode || "Other";
      if (!map.has(code)) map.set(code, []);
      map.get(code).push(y);
    }
    for (const [, list] of map) {
      list.sort(
        (a, b) =>
          Number(b.featured || 0) - Number(a.featured || 0) ||
          (b.length ?? 0) - (a.length ?? 0),
      );
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      const ai = CLASS_LETTER_ORDER.indexOf(a);
      const bi = CLASS_LETTER_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [yachts]);

  // Flat, sorted list used for brands that aren't organized by class.
  const sortedYachts = useMemo(
    () =>
      [...yachts].sort(
        (a, b) =>
          Number(b.featured || 0) - Number(a.featured || 0) ||
          (b.length ?? 0) - (a.length ?? 0),
      ),
    [yachts],
  );

  // Only show "not found" once a fetch actually failed; before the fetch
  // starts (first render) currentLoading is still false and we must not
  // flash the error screen.
  if (!brand && !currentLoading && error) {
    return (
      <PageWrapper>
        <HeroSection>
          <Container>
            <NotFound>
              <h2>Brand Not Found</h2>
              <p>{error || "The brand you're looking for doesn't exist."}</p>
              <PrimaryCta to="/brands">View All Brands</PrimaryCta>
            </NotFound>
          </Container>
        </HeroSection>
      </PageWrapper>
    );
  }

  // Loading state: show a hero skeleton instead of a blank page while the
  // brand payload is fetched (a blank page reads as "the site is broken",
  // especially on slow mobile connections).
  if (!brand) {
    return (
      <PageWrapper>
        <HeroSection>
          <Container>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "18px",
                padding: "56px 0",
              }}
            >
              <SkeletonBlock $height="12px" $width="180px" />
              <SkeletonBlock $height="52px" $width="45%" />
              <SkeletonBlock $height="20px" $width="60%" />
              <SkeletonBlock $height="16px" $width="30%" />
              <SkeletonBlock
                $height="48px"
                $width="280px"
                style={{ marginTop: "12px" }}
              />
            </div>
          </Container>
        </HeroSection>
      </PageWrapper>
    );
  }

  const category = CATEGORY_COPY[brand.category] || CATEGORY_COPY.yacht;
  const heroBg = brand.primaryColor || null;

  const heroYacht = yachts.find((y) => y.featured) || yachts[0] || null;
  const heroImageSrc = pickYachtHeroImage(heroYacht);

  const isClassBased = CLASS_BASED_BRANDS.has(brand.slug);

  const renderModelCard = (yacht) => (
    <motion.div
      key={yacht.id}
      variants={scaleIn}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      style={{ height: "100%" }}
    >
      <Link
        to={`/yachts/${yacht.id}`}
        style={{ display: "block", height: "100%" }}
      >
        <ModelCard>
          <GlassCardImage $ratio="16 / 10">
            <ModelTagRow>
              {yacht.featured && (
                <GlassTag $variant="featured">Featured</GlassTag>
              )}
              {(() => {
                const tag =
                  CONDITION_TAG[yacht.condition] || CONDITION_TAG.availableNow;
                return <GlassTag $variant={tag.variant}>{tag.label}</GlassTag>;
              })()}
            </ModelTagRow>
            {pickCardImage(yacht) && (
              <img
                src={pickCardImage(yacht)}
                alt={yacht.name}
                loading="lazy"
                decoding="async"
                onError={cardImageFallback(yacht)}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
          </GlassCardImage>
          <div
            style={{
              padding: "1.5rem",
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            {yacht.subtype && <ModelSubtype>{yacht.subtype}</ModelSubtype>}
            <ModelName>{yacht.name}</ModelName>
            <ModelSpecs>
              <div>
                <span>Length</span>
                <p>{yacht.length ? `${yacht.length}m` : "—"}</p>
              </div>
              <div>
                <span>Guests</span>
                <p>{yacht.guests ?? "—"}</p>
              </div>
              <div>
                <span>Cabins</span>
                <p title={yacht.cabins || undefined}>
                  {formatCabins(yacht.cabins) ?? "—"}
                </p>
              </div>
            </ModelSpecs>
          </div>
        </ModelCard>
      </Link>
    </motion.div>
  );

  return (
    <PageWrapper>
      <HeroSection $bg={heroBg}>
        <Container>
          <Breadcrumbs>
            <Link to="/">Home</Link> / <Link to="/brands">Brands</Link> /{" "}
            {brand.name}
          </Breadcrumbs>

          <StaggerReveal>
            <HeroGrid>
              <HeroLeft>
                <MotionItem $variants={fadeUpSmall}>
                  <Eyebrow>{category.label} Manufacturer</Eyebrow>
                </MotionItem>
                <MotionItem $variants={fadeUp}>
                  <BrandName>{brand.name}</BrandName>
                </MotionItem>
                {brand.tagline && (
                  <MotionItem $variants={fadeUpSmall}>
                    <Tagline>{brand.tagline}</Tagline>
                  </MotionItem>
                )}

                <MotionItem $variants={fadeUpSmall}>
                  <BrandMeta>
                    {brand.origin && (
                      <MetaItem>
                        <span>Origin</span>
                        <p>{brand.origin}</p>
                      </MetaItem>
                    )}
                    <MetaItem>
                      <span>Models</span>
                      <p>{yachts.length}</p>
                    </MetaItem>
                  </BrandMeta>
                </MotionItem>

                <MotionItem $variants={fadeUpSmall}>
                  <HeroCtaRow>
                    <PrimaryCta
                      to={`${category.listingPath}?brand=${encodeURIComponent(
                        brand.name,
                      )}`}
                    >
                      Browse {brand.name} {category.label}
                    </PrimaryCta>
                    {brand.website && (
                      <SecondaryCta
                        href={brand.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span>Visit Website</span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <line x1="7" y1="17" x2="17" y2="7" />
                          <polyline points="7 7 17 7 17 17" />
                        </svg>
                      </SecondaryCta>
                    )}
                  </HeroCtaRow>
                </MotionItem>
              </HeroLeft>

              <MotionItem $variants={fadeRight}>
                <HeroVisual>
                  {heroImageSrc ? (
                    <img src={heroImageSrc} alt={`${brand.name} flagship`} />
                  ) : (
                    <HeroVisualPlaceholder>{brand.name}</HeroVisualPlaceholder>
                  )}

                  <HeroFooter>
                    {(() => {
                      const SvgLogo = BRAND_LOGO_SVGS[brand.slug];
                      if (SvgLogo) {
                        return (
                          <HeroLogoMark>
                            <SvgLogo aria-label={brand.name} />
                          </HeroLogoMark>
                        );
                      }
                      if (brand.logo) {
                        return (
                          <HeroLogoMark>
                            <img src={mediaUrl(brand.logo)} alt={brand.name} />
                          </HeroLogoMark>
                        );
                      }
                      return null;
                    })()}
                    {heroYacht && (
                      <HeroVisualCaption>
                        Flagship · {heroYacht.name}
                      </HeroVisualCaption>
                    )}
                  </HeroFooter>
                </HeroVisual>
              </MotionItem>
            </HeroGrid>
          </StaggerReveal>
        </Container>
      </HeroSection>

      {brand.bio && (
        <Section>
          <Container>
            <StaggerReveal>
              <MotionItem $variants={fadeUpSmall}>
                <SectionTitle>About {brand.name}</SectionTitle>
              </MotionItem>
              <MotionItem $variants={fadeUp}>
                <Bio>{brand.bio}</Bio>
              </MotionItem>
            </StaggerReveal>
          </Container>
        </Section>
      )}

      {yachts.length > 0 && (
        <Section style={{ background: "var(--off-white, #f6f7f8)" }}>
          <Container>
            <StaggerReveal>
              <MotionItem $variants={fadeUpSmall}>
                <SectionTitle>The Lineup</SectionTitle>
              </MotionItem>
            </StaggerReveal>

            {isClassBased ? (
              classGroups.map(([code, list]) => (
                <ClassBlock key={code}>
                  <ClassHeader>
                    <ClassTitle>{classDisplayName(code)}</ClassTitle>
                    <ClassMeta>
                      {list.length} {list.length === 1 ? "model" : "models"}
                      {list[0]?.subtype ? ` · ${list[0].subtype}` : ""}
                    </ClassMeta>
                  </ClassHeader>
                  <Grid>{list.map(renderModelCard)}</Grid>
                </ClassBlock>
              ))
            ) : (
              <Grid>{sortedYachts.map(renderModelCard)}</Grid>
            )}
          </Container>
        </Section>
      )}
    </PageWrapper>
  );
}

export default BrandDetailsPage;
